'use client';
import { useWishList } from '@/account/api/query/wishlist';
import Button from '@/components/base/button/button';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import { BasketNavElement } from '@/features/navigation/components/main-section/basket-nav-element';
import { Search } from '@/features/navigation/components/search/search';
import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import { useNavigationLogoUrl } from '@/navigation/hooks/use-navigation-logo-url';
import { ACCOUNT_ROUTES, LOGIN_ROUTES, RESERVATION_ROUTES } from '@/routes';
import { useIsCustomer } from '@/utils/is-customer';
import { Heart, UserCircle } from '@phosphor-icons/react';
import { List, User } from '@phosphor-icons/react/dist/ssr';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import logoSquare from 'public/images/logo/menu/logo-square.svg';
import { memo, useState } from 'react';
import { NavElement } from './nav-element';

const { mainSectionWrapper, mainSectionStickyStyles } = {
  mainSectionWrapper:
    'flex h-clickable w-full items-center justify-between pl-xs pr-xxs xl:container xs:pl-s s:h-[66px] s:pl-[30px] s:pr-s m:pl-l m:pr-m l:h-[72px] l:items-end l:pl-[48px] l:pr-[30px] xl:pl-0 xl:pr-0',
  mainSectionStickyStyles:
    'group-[.sticky]:h-[42px] group-[.sticky]:xxl:!container group-[.sticky]:s:h-clickable group-[.sticky]:l:h-clickable-large group-[.sticky]:xl:w-full group-[.sticky]:xl:pl-[48px] group-[.sticky]:xl:pr-l group-[.sticky]:relative',
};

const selector = ({ isMobileMenuOpen, openMobileMenu }: NavigationStore) => ({
  isMobileMenuOpen,
  openMobileMenu,
});

const MainSection = () => {
  const t = useTranslations('navigation.main_section');
  const { data } = useWishList();

  const [shouldHideVoucherButton, setShouldHideVoucherButton] = useState(false);
  const { isMobileMenuOpen, openMobileMenu } = useNavigationStore(selector);

  const logoUrl = useNavigationLogoUrl();
  const loggedIn = useIsCustomer();

  return (
    <>
      <div className={clsx(mainSectionWrapper, mainSectionStickyStyles)}>
        <div className="pointer-events-none absolute top-0 hidden h-full w-full backdrop-blur-sm group-[.sticky]:block" />
        <div
          className={clsx(
            'flex h-full w-full l:items-end items-center justify-between l:gap-xl xxl:gap-3xl group-[.sticky]:l:items-center group-[.sticky]:l:gap-xxl xl:gap-xxl group-[.sticky]:xl:gap-3xl',
          )}
        >
          <Button
            variant="tertiary-no-frame"
            aria-label={t('logo_label')}
            className={clsx(
              '-my-xxs !pl-0 mr-auto flex h-full max-h-clickable-large w-full xs:w-auto xs:shrink-0 items-start group-[.sticky]:m:h-clickable group-[.sticky]:xxl:basis-auto',
            )}
            href="/"
            isLink
          >
            <Image
              src={logoUrl}
              alt=""
              priority
              className={clsx(
                'h-[42px] l:h-clickable-large s:h-clickable l:w-[135px] s:w-[108px] w-[95px] group-[.sticky]:xxl:hidden group-[.sticky]:m:h-clickable group-[.sticky]:w-full group-[.sticky]:max-w-[81px] group-[.sticky]:s:max-w-[95px]',
              )}
              width={60}
              height={24}
            />
            <Image
              src={logoSquare}
              alt=""
              priority
              className={clsx(
                'hidden h-clickable w-[59px] group-[.sticky]:xxl:block',
              )}
              width={20}
              height={24}
            />
          </Button>
          <div
            className={clsx(
              'flex h-full w-full l:items-end items-center justify-end l:gap-0 s:gap-xs l:pb-xxs group-[.sticky]:l:items-center group-[.sticky]:xxl:gap-m group-[.sticky]:l:pb-0',
            )}
          >
            <Search setShouldHideVoucherButton={setShouldHideVoucherButton} />
            {!shouldHideVoucherButton && (
              <Button
                variant="tertiary"
                className="l:mr-m l:block hidden w-max shrink-0 group-[.sticky]:hidden xl:mr-l "
                isLink
                href={RESERVATION_ROUTES.RESERVATION}
              >
                {t('i_have_a_voucher')}
              </Button>
            )}
            <div
              className={clsx(
                'xl:-mr-s flex h-full l:items-end items-center l:gap-0 s:gap-xs group-[.sticky]:l:items-center xl:gap-xxs',
              )}
            >
              {loggedIn ? (
                <NavElement
                  text={t('my_account')}
                  icon={UserCircle}
                  redirect={ACCOUNT_ROUTES.DASHBOARD}
                />
              ) : (
                <NavElement
                  text={t('log_in')}
                  icon={User}
                  redirect={LOGIN_ROUTES.LOGIN}
                />
              )}
              <NavElement
                text={t('favorites')}
                data-testid="favorites_nav_element"
                icon={Heart}
                badgeNumber={data?.pagination?.count}
                redirect={ACCOUNT_ROUTES.FAVORITES}
                type="favorite"
              />
              <BasketNavElement />
            </div>
          </div>
        </div>
        <div className="s:mx-xs l:hidden h-full w-xs min-w-xs">
          <div className="m-auto h-full w-px bg-decorative" />
        </div>
        <div
          className={clsx(
            'l:hidden h-full group-[.sticky]:l:ml-s group-[.sticky]:flex group-[.sticky]:items-center',
          )}
        >
          <Button
            variant="tertiary-no-frame"
            className="h-full l:h-clickable l:w-auto w-clickable l:px-s"
            data-ui="hamburger-button"
            onClick={openMobileMenu}
            aria-expanded={isMobileMenuOpen}
          >
            <PhosphorIcon
              Icon={List}
              variant="regular"
              className="icon-m s:icon-l w-auto fill-strong"
            />
            <span className="sr-only">{t('open_menu')}</span>
          </Button>
        </div>
      </div>
    </>
  );
};

export default memo(MainSection);
